import React, { useState, FormEvent } from 'react';
import { Supplier } from '@/types';

interface EditSupplierFormProps {
  supplier: Supplier;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const EditSupplierForm = ({ 
  supplier, 
  onSave,
  onCancel 
}: EditSupplierFormProps) => {
  const [formData, setFormData] = useState({
    name: supplier.name,
    placeholderToken: supplier.placeholderToken || '',
    completeUrlTemplate: supplier.completeUrlTemplate || '',
    terminateUrlTemplate: supplier.terminateUrlTemplate || '',
    quotaFullUrlTemplate: supplier.quotaFullUrlTemplate || '',
    securityTerminateUrlTemplate: supplier.securityTerminateUrlTemplate || '',
    // Research Defender settings
    rdEnabled: supplier.rdEnabled ?? true,
    rdTriggerScore: supplier.rdTriggerScore ?? 0,
    rdBlockDuplicates: supplier.rdBlockDuplicates ?? false,
    enableGeoBlocker: supplier.enableGeoBlocker ?? false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let newValue: string | number | boolean = value;
    
    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'range' || name === 'rdTriggerScore') {
      newValue = Number(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const confirmed = window.confirm(
      'Are you sure you want to make these changes? This could affect other surveys that are already linked to this supplier.'
    );
    if (!confirmed) {
      return;
    }

    const updateData = {
      name: formData.name,
      placeholderToken: formData.placeholderToken || null,
      completeUrlTemplate: formData.completeUrlTemplate || null,
      terminateUrlTemplate: formData.terminateUrlTemplate || null,
      quotaFullUrlTemplate: formData.quotaFullUrlTemplate || null,
      securityTerminateUrlTemplate: formData.securityTerminateUrlTemplate || null,
      // Research Defender settings - ensure rdTriggerScore is a number
      rdEnabled: formData.rdEnabled,
      rdTriggerScore: Number(formData.rdTriggerScore),
      rdBlockDuplicates: formData.rdBlockDuplicates,
      enableGeoBlocker: formData.enableGeoBlocker,
    };
    
    console.log('Submitting supplier update:', updateData);
    console.log('Form data being sent:', JSON.stringify(updateData, null, 2));
    onSave(updateData);
  };

  return (
    <form id="supplier-edit-form" onSubmit={handleSubmit} className="contents">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-2">Supplier Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input"
              required
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-500 mb-2">Placeholder Token</label>
            <input
              type="text"
              name="placeholderToken"
              value={formData.placeholderToken}
              onChange={handleChange}
              className="input"
              placeholder="e.g. [%RID%]"
            />
          </div>
          
          <div className="md:col-span-2">
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">URL Templates</h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Complete URL</label>
                  <textarea
                    name="completeUrlTemplate"
                    value={formData.completeUrlTemplate}
                    onChange={handleChange}
                    className="input h-16 resize-none"
                    placeholder="https://example.com/complete?pid={panelId}"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL for successful survey completion</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Terminate URL</label>
                  <textarea
                    name="terminateUrlTemplate"
                    value={formData.terminateUrlTemplate}
                    onChange={handleChange}
                    className="input h-16 resize-none"
                    placeholder="https://example.com/terminate?pid={panelId}"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL for terminated surveys</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Quota Full URL</label>
                  <textarea
                    name="quotaFullUrlTemplate"
                    value={formData.quotaFullUrlTemplate}
                    onChange={handleChange}
                    className="input h-16 resize-none"
                    placeholder="https://example.com/quota-full?pid={panelId}"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL when quota is full</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Security Terminate URL</label>
                  <textarea
                    name="securityTerminateUrlTemplate"
                    value={formData.securityTerminateUrlTemplate}
                    onChange={handleChange}
                    className="input h-16 resize-none"
                    placeholder="https://example.com/security-terminate?pid={panelId}"
                  />
                  <p className="text-xs text-gray-500 mt-1">URL for security terminations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Research Defender Settings Section */}
          <div className="md:col-span-2">
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Research Defender Settings</h3>
              <p className="text-xs text-gray-500 mb-4">Default settings for all collections using this supplier. Collections can override these values.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="rdEnabled"
                      checked={formData.rdEnabled}
                      onChange={handleChange}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Enable Research Defender</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Run fraud detection on respondents</p>
                </div>
                
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="rdBlockDuplicates"
                      checked={formData.rdBlockDuplicates}
                      onChange={handleChange}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Block Duplicate Participants</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Security terminate duplicate respondents</p>
                </div>
                
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="enableGeoBlocker"
                      checked={formData.enableGeoBlocker}
                      onChange={handleChange}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Enable Geo Blocker</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">Block respondents with country mismatch</p>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    RD Trigger Score: {formData.rdTriggerScore}
                  </label>
                  <input
                    type="range"
                    name="rdTriggerScore"
                    min="0"
                    max="100"
                    value={formData.rdTriggerScore}
                    onChange={handleChange}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0 (Lenient)</span>
                    <span>100 (Strict)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Respondents with scores at or above this threshold will be security terminated</p>
                </div>
              </div>
            </div>
          </div>

          </div>
          
          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <button type="button" onClick={onCancel} className="md-btn md-btn-outlined flex-1">
              Cancel
            </button>
            <button type="submit" className="md-btn md-btn-primary flex-1">
              Save Supplier
            </button>
          </div>
      </div>
    </form>
  );
};

export default EditSupplierForm;